import { defineStore } from 'pinia';
import { computed, ref, type Ref } from 'vue';
import dayjs, { type Dayjs } from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { AempschedDay, AempschedFilters } from 'Types/aempsched/aempschedDay';
import { scheduleService } from '@/services/schedulesService';
import type { IFilterSelection, FilterGroupIds } from 'Types/aempsched/aempschedDay';
import { logError, logWarn } from '@/utils/logger';

dayjs.extend(weekOfYear);
dayjs.extend(utc);
dayjs.extend(timezone);

export const useScheduleStore = defineStore('schedule', () => {
	const today: Ref<Dayjs> = ref(dayjs());
	const selectedDay: Ref<Dayjs> = ref(dayjs());
	const days: Ref<AempschedDay[]> = ref([]);
	const lastStart: Ref<Dayjs> = ref(dayjs());
	const lastEnd: Ref<Dayjs> = ref(dayjs());
	const loading: Ref<boolean> = ref(true);
	const filters: Ref<AempschedFilters> = ref({});
	const selectedFilters: Ref<IFilterSelection[]> = ref([]);

	const selectedDayClasses = computed(() => {
		const selected = days.value.find((day) => day.dayObject.isSame(selectedDay.value, 'day'));
		return selected?.classes ?? [];
	});

	const setSelectedDay = (day: Dayjs) => {
		selectedDay.value = day;
	};

	const daysInit = () => {
		const dayDataStart = today.value.clone().startOf('week').day(0);

		for (let i = 0; i < 7 * 8; i++) {
			const day: AempschedDay = {
				dayObject: dayDataStart.clone().add(i, 'day'),
				classes: []
			};
			days.value.push(day);
		}
	};

	const getData = async (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
		lastStart.value = start;
		lastEnd.value = end;

		loading.value = true;
		try {
			loadSelectedFiltersFromUrl();

			const results = await scheduleService.getSchedule(
				start.format('YYYY-MM-DD'),
				end.format('YYYY-MM-DD'),
				selectedFilters.value
			);
			const dates = results.data.dates;
			if (dates.length > 0) {
				dates.forEach((d: any): void => {
					const date = dayjs.tz(d.date);
					const update = days.value.find((day) => day.dayObject.isSame(date, 'day'));
					if (update) {
						update.classes = d.classes;
					}
				});
			}
			filters.value = results.data.filters;

			if (!Object.keys(filters.value).length && selectedFilters.value.length) {
				// Filters came back empty. Need to reset them.
				// TODO: Make this flow impossible, or provide some info to the
				// user explaining it. Today it can happen when user's new
				// filter choice makes an existing filter invalid.
				// For example:
				// - start with empty filters
				// - in "Interests" check "Barre" and "Group Fitness"
				// - in "Experience Level" check "Beginner"
				// - in "Interests" uncheck "Group Fitness"
				// Dead end. There is no beginner barre class.
				logWarn('API returned empty filters. Clearing all filters.', { filters: filters.value });
				resetFilters();
			}
		} catch (error) {
			logError('Error fetching schedule data', { filters: filters.value }, error);
		} finally {
			loading.value = false;
		}
	};

	const getDataAgain = async () => {
		await getData(lastStart.value, lastEnd.value);
	};

	const loadSelectedFiltersFromUrl = () => {
		const urlParams = new URLSearchParams(window.location.search);
		const newSelectedFilters: IFilterSelection[] = [];

		function pushFoundFilter(group: FilterGroupIds) {
			return (name: string) => {
				newSelectedFilters.push({ group, name });
			};
		}

		urlParams.getAll('ages').forEach(pushFoundFilter('ages'));
		urlParams.getAll('experienceLevels').forEach(pushFoundFilter('experienceLevels'));
		urlParams.getAll('interests').forEach(pushFoundFilter('interests'));
		urlParams.getAll('timesOfDay').forEach(pushFoundFilter('timesOfDay'));

		selectedFilters.value = newSelectedFilters;
	};

	const resetFilters = () => {
		const groups: FilterGroupIds[] = [
			'ages',
			'experienceLevels',
			'interests',
			'timesOfDay',
			'instructors'
		];
		const url = new URL(window.location.href);
		groups.forEach((group) => url.searchParams.delete(group));
		window.history.replaceState(null, '', url);
		getDataAgain();
	};

	return {
		today,
		selectedDay,
		days,
		filters,
		selectedFilters,
		selectedDayClasses,
		loading,

		setSelectedDay,
		daysInit,
		getData,
		getDataAgain,
		resetFilters
	};
});
